import { SearchForm } from "components/forms/SearchForm";
import { DynamicContent } from "components/information-lister-pages/DynamicContent";
import { AdvancedSearchFields } from "components/information-lister-pages/HomeContent/elements/AdvancedSearchFields";
import { FixedBottomBar } from "components/information-lister-pages/HomeContent/elements/FixedBottomBar";
import { ResponsiveBlock } from "components/ResponsiveBlock";
import { Modal } from "components/shared/Modal/Modal";
import dynamic from "next/dynamic";
import React, { useState } from "react";
import { BasicSearchFields } from "components/information-lister-pages/HomeContent/elements/BasicSearchFields";
import { useIntersectionObserver } from "lib/intersectionObserver";
import { HomeSearchHeader } from "components/home-search-header";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";
import { Usps } from "ui/organisms/usps/usps";
import styled from "styled-components";
import { StyledLazyImage } from "components/information-lister-pages/LinkListBlock/elements/ListItem/elements/StyledLazyImage";
import { TextOnBlack } from "components/information-lister-pages/LinkListBlock/elements/ListItem/elements/Text";
import { Number } from "components/Number";
import Link from "next/link";
import { Title } from "ui/atoms";
import { About } from "components/information-lister-pages/PlaceholderBlock/elements/About";
import { FindDealer } from "components/information-lister-pages/PlaceholderBlock/elements/FindDealer";
import { LinkToPost } from "components/information-lister-pages/LinkListBlock/elements/LinkToPost";
import { useMediaContext } from "lib/media-query";
import { Screens } from "helpers/screens";
import { Tips } from "components/Tips";
import { VehicleTypeSection } from "ui/organisms/vehicle-type/index.tsx";
import { Search } from "./elements/Search";
import { AdvancedSearchButton } from "./elements/AdvancedSearchButton";

const Banner = dynamic(() => import("components/Banner"), { ssr: false });

const contentfulBlocksSection = new Map([
    ["snel-naar", "7i7hW6vdAGYPUAhuUuVhJV"],
    ["news", "1VfjoQQD2pD3XK1EBcp47p"],
    ["carrosseries-home", "2m6D1ScixwFqfQ2q0xSrbf"],
    ["tips", "4ieqYrbpk7MULt3nFKmPxK"]
]);

const sendSectionDataToDataLayer = ({ sectionType, sectionTitle }) => {
    const sectionsObject = {
        event: "homepage_section_view",
        sectionType,
        sectionTitle
    };
    datalayerWrapper(sectionsObject);
};

const SnelNaarContainer = styled.section`
    display: grid;
    scroll-snap-type: x mandatory;
    overflow-x: scroll;
    gap: 2rem;
    grid-template-columns: repeat(3, 1fr);

    ::-webkit-scrollbar {
        display: none;
        width: 0;
    }
`;

const TextWrapper = styled.div`
    display: flex;
    padding: 1rem;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.white};
    span {
        color: ${({ theme }) => theme.colors.black};
        font-family: ${({ theme }) => theme.typography.fontFamily.primary};
        font-size: 1.125rem;
        font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    }
`;

const searchResultsPathRegExp = /^\/aanbod/;

const SnelNaarCard = styled.div`
    display: grid;
    cursor: pointer;
    min-height: 15.31rem;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.03);
    border-radius: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.tertiary[500]};
    background-color: ${({ theme }) => theme.colors.primary[600]};
    grid-template-rows: 3fr 1fr;
    width: 18.75rem;

    @media (${Screens.get("lg")}) {
        min-width: 26.83rem;
    }
`;

const ContentfulSectionHeader = styled(Title)`
    color: ${({ theme }) => theme.colors.black};
    font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    font-size: 1.875rem;
    padding: 0 1rem;

    @media (${Screens.get("lg")}) {
        padding: 0 3.5rem;
    }
`;

const NewsSectionContainer = styled.section`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 1rem;

    @media (${Screens.get("lg")}) {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        padding: 0 3.5rem;
    }
`;

const NewsCard = styled.a`
    display: flex;
    flex-direction: row;
    gap: 1.25rem;

    img {
        border-radius: 0.75rem 0;
        max-width: 5rem;
        max-height: 5rem;
    }

    span {
        color: var(--at-colors-blacks-700-black, var(--at-colors-blacks-700-black, #000));
        font-family: Lato;
        font-size: 1.25rem;
        font-style: normal;
        font-weight: 700;
        line-height: 1.75rem; /* 140% */
        border-bottom: 1px solid ${({ theme }) => theme.colors.tertiary[200]};
    }

    :focus {
        outline: none;
        text-decoration: none;
    }
`;

const VehicleTypeSectionContainer = styled.div`
    display: grid;
    padding: 0 1rem;
    gap: 2rem;

    @media (${Screens.get("lg")}) {
        padding: 0 3.5rem;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3.5rem;
    margin: 0 0 3.5rem 0;
`;

export const HomeContent = ({ content }) => {
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    useIntersectionObserver(sendSectionDataToDataLayer, true, 500);

    const snelNaarContent = content?.dynamicContentList?.find(
        (item) => item._id === contentfulBlocksSection.get("snel-naar")
    );

    const newsContent = content?.dynamicContentList?.find(
        (item) => item._id === contentfulBlocksSection.get("news")
    );

    const tipsContent = content?.dynamicContentList?.find(
        (item) => item._id === contentfulBlocksSection.get("tips")
    );

    const mediaContext = useMediaContext();
    const isMobilePhone =
        mediaContext.isMobile || mediaContext.isMobilePlus || mediaContext.isTabletPlus;

    return (
        <Wrapper>
            <ResponsiveBlock>
                <SearchForm>
                    <HomeSearchHeader />
                    <Modal onClose={() => setShowAdvancedSearch(false)} isOpen={showAdvancedSearch}>
                        <Search advanced>
                            <BasicSearchFields />
                            <AdvancedSearchButton
                                expanded
                                onClick={() => setShowAdvancedSearch(false)}
                            />
                            <AdvancedSearchFields />
                        </Search>
                        <FixedBottomBar onCancel={() => setShowAdvancedSearch(false)} />
                    </Modal>
                </SearchForm>
            </ResponsiveBlock>
            <ResponsiveBlock>
                <Usps />
            </ResponsiveBlock>
            <ResponsiveBlock>
                <div className="banner-home-top dfp-banner">
                    <Banner id="banner-home-top" />
                </div>
            </ResponsiveBlock>
            {!!tipsContent && (
                <ResponsiveBlock>
                    <Tips tips={tipsContent?.links.slice(0, 3)} />
                </ResponsiveBlock>
            )}
            <ResponsiveBlock>
                <ContentfulSectionHeader as="h2">Snel naar</ContentfulSectionHeader>
                <VehicleTypeSectionContainer>
                    <SnelNaarContainer>
                        {snelNaarContent &&
                            snelNaarContent.links.map((link) => {
                                const { search, pathname } = new URL(link.url);

                                return (
                                    <Link key={link.title} passHref href={pathname + search}>
                                        <SnelNaarCard>
                                            <StyledLazyImage
                                                src={`${
                                                    link.image?.fields
                                                        ? link.image.fields.file.url
                                                        : link.image?.file?.url
                                                }?w=800`}
                                                alt={link.title}
                                                style={{
                                                    alignSelf: "center",
                                                    justifySelf: "center",
                                                    height: 119,
                                                    width: 166,
                                                    background: "transparent"
                                                }}
                                            />
                                            <TextWrapper>
                                                {link.title &&
                                                    link.title
                                                        .split("/")
                                                        .map((subtitle) => (
                                                            <span key={subtitle}>{subtitle}</span>
                                                        ))}
                                                {searchResultsPathRegExp.test(pathname) &&
                                                    link.total > 0 && (
                                                        <TextOnBlack>
                                                            <Number value={link.total} /> AUTO'S
                                                        </TextOnBlack>
                                                    )}
                                            </TextWrapper>
                                        </SnelNaarCard>
                                    </Link>
                                );
                            })}
                    </SnelNaarContainer>
                    <VehicleTypeSection />
                </VehicleTypeSectionContainer>
            </ResponsiveBlock>
            <ResponsiveBlock>
                <FindDealer />
            </ResponsiveBlock>
            <ResponsiveBlock>
                <ContentfulSectionHeader as="h2">Blog</ContentfulSectionHeader>
                <NewsSectionContainer>
                    {newsContent &&
                        newsContent.links.slice(0, 4).map((item) => {
                            if (!isMobilePhone) {
                                return <LinkToPost key={item._id} {...item} />;
                            }

                            const url = [
                                "/blog",
                                item.categories.filter((c) => c._type === "category").shift()?.slug,
                                item.slug
                            ].join("/");

                            return (
                                <NewsCard href={url} key={item.title}>
                                    <StyledLazyImage
                                        src={`${
                                            item.attachment?.fields
                                                ? item.attachment.fields.file.url
                                                : item.attachment?.file?.url
                                        }?w=800`}
                                        alt={item.title}
                                    />
                                    <span>{item.title}</span>
                                </NewsCard>
                            );
                        })}
                </NewsSectionContainer>
            </ResponsiveBlock>
            <ResponsiveBlock>
                <About />
            </ResponsiveBlock>
            <ResponsiveBlock>
                <div className="banner-home-footer dfp-banner">
                    <Banner id="banner-home-footer" />
                </div>
            </ResponsiveBlock>
        </Wrapper>
    );
};

HomeContent.displayName = "HomeContent";

HomeContent.getContextProps = async (content) => {
    return DynamicContent.getContextProps(content);
};
